import './App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { ToastContainer } from 'react-toastify';
import { ProtectedRoutes } from './Routes/ProtectedRoutes';
import MainLayout from './Components/Layouts/MainLayout';
import Loader from './Pages/Loader';
import { ConfirmProvider } from 'material-ui-confirm';
import { ThemeProvider } from '@emotion/react';
import { lightTheme } from './Theme/theme';
import "react-toastify/dist/ReactToastify.css";
import { Box, CircularProgress } from '@mui/material';

// Landing Page imports (lazy)
import LandingLayout from './Components/Landing/LandingLayout';
const LandingHome = lazy(() => import('./Pages/Landing/LandingHome'));
const Events = lazy(() => import('./Pages/Landing/Events'));
const EventDetails = lazy(() => import('./Pages/Landing/EventDetails'));
const AboutUs = lazy(() => import('./Pages/Landing/AboutUs'));
const ContactUs = lazy(() => import('./Pages/Landing/ContactUs'));
const PressAndMedia = lazy(() => import('./Pages/Landing/PressAndMedia'));
const PrivacyPolicy = lazy(() => import('./Pages/Landing/PrivacyPolicy'));
const TermsandConditions = lazy(() => import('./Pages/Landing/TermsandConditions'));
const PostGenerator = lazy(() => import('./Pages/Landing/PostGenerator'));
const MemberPostGenerator = lazy(() => import('./Pages/Landing/MemberPostGenerator'));
const PublicPayment = lazy(() => import('./Pages/PublicPayment'));

// Admin Pages (lazy)
const Login = lazy(() => import('./Pages/Login'));
const Dashboard = lazy(() => import('./Pages/Dashboard'));
const AddMember = lazy(() => import('./Pages/Member/AddMember'));
const MemberDetails = lazy(() => import('./Pages/Member/MemberDetails'));
const Claims = lazy(() => import('./Pages/Claims'));
const Documents = lazy(() => import('./Pages/Documents'));
const HelpDesk = lazy(() => import('./Pages/HelpDesk'));
const Reports = lazy(() => import('./Pages/Reports'));
const Settings = lazy(() => import('./Pages/Settings'));
const DocumentView = lazy(() => import('./Pages/DocumentView'));
const DocumentsViewStatus = lazy(() => import('./Pages/DocumentStatusWise'));
const TransactionHistory = lazy(() => import('./Pages/TransactionHistory'));
const AllTransaction = lazy(() => import('./Pages/AllTransaction'));
const OrganisationMembers = lazy(() => import('./Pages/OrganisationMembers'));
const ActiveMembers = lazy(() => import('./Pages/ActiveMembers'));
const OrganisationPayment = lazy(() => import('./Pages/OrganisationPayment'));
const OrganisationManagement = lazy(() => import('./Pages/OrganisationManagement'));
const Website = lazy(() => import('./Pages/Website'));
const Template = lazy(() => import('./Pages/Template'));
const Communication = lazy(() => import('./Pages/Communication'));
const Reminders = lazy(() => import('./Pages/Reminders'));
const ReferenceTracking = lazy(() => import('./Pages/ReferenceTracking'));

// Expense Module (lazy)
const ExpensesList = lazy(() => import('./Pages/Expenses').then(m => ({ default: m.ExpensesList })));
const ExpenseForm = lazy(() => import('./Pages/Expenses').then(m => ({ default: m.ExpenseForm })));
const ExpensesDashboard = lazy(() => import('./Pages/Expenses').then(m => ({ default: m.ExpensesDashboard })));
const ExpenseCategories = lazy(() => import('./Pages/Expenses').then(m => ({ default: m.ExpenseCategories })));
const ExpenseReports = lazy(() => import('./Pages/Expenses').then(m => ({ default: m.ExpenseReports })));

// Post Generator Module (lazy)
const PostCategories = lazy(() => import('./Pages/PostGenerator').then(m => ({ default: m.PostCategories })));
const PostTemplates = lazy(() => import('./Pages/PostGenerator').then(m => ({ default: m.PostTemplates })));
const TemplateEditor = lazy(() => import('./Pages/PostGenerator').then(m => ({ default: m.TemplateEditor })));

const PageFallback = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <CircularProgress size={36} />
  </Box>
);

function App() {
  return (
    <BrowserRouter>
      <Loader />
      <ToastContainer />
      <ThemeProvider theme={lightTheme}>
        <ConfirmProvider
          defaultOptions={{
            confirmationButtonProps: {
              variant: "contained",
            },
            cancellationButtonProps: {
              variant: "outlined",
            },
          }}
        >
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Landing Page Routes */}
            <Route path="/" element={<LandingLayout><LandingHome /></LandingLayout>} />
            <Route path="/events" element={<LandingLayout><Events /></LandingLayout>} />
            <Route path="/event/:id" element={<LandingLayout><EventDetails /></LandingLayout>} />
            <Route path="/about-us" element={<LandingLayout><AboutUs /></LandingLayout>} />
            <Route path="/contact-us" element={<LandingLayout><ContactUs /></LandingLayout>} />
            <Route path="/press-and-media" element={<LandingLayout><PressAndMedia /></LandingLayout>} />
            <Route path="/privacy-policy" element={<LandingLayout><PrivacyPolicy /></LandingLayout>} />
            <Route path="/terms-and-conditions" element={<LandingLayout><TermsandConditions /></LandingLayout>} />
            <Route path="/post-generator" element={<LandingLayout><PostGenerator /></LandingLayout>} />
            <Route path="/member/post-generator" element={<LandingLayout><MemberPostGenerator /></LandingLayout>} />

            {/* Public Payment Route (WhatsApp Renewal Links) */}
            <Route path="/pay/:shortCode" element={<PublicPayment />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<MainLayout><Login /></MainLayout>} />
            <Route element={<ProtectedRoutes />}>
              <Route path="/admin" element={<MainLayout><Dashboard /></MainLayout>} />
              <Route path="/admin/addMember" element={<MainLayout><AddMember /></MainLayout>} />
              <Route path="/admin/member/:id" element={<MainLayout><MemberDetails /></MainLayout>} />
              <Route path="/admin/claims" element={<MainLayout><Claims /></MainLayout>} />
              <Route path="/admin/documents" element={<MainLayout><Documents /></MainLayout>} />
              <Route path="/admin/documents-view-status" element={<MainLayout><DocumentsViewStatus /></MainLayout>} />
              <Route path="/admin/documents-view" element={<MainLayout><DocumentView /></MainLayout>} />
              <Route path="/admin/helpDesk" element={<MainLayout><HelpDesk /></MainLayout>} />
              <Route path="/admin/reports" element={<MainLayout><Reports /></MainLayout>} />
              <Route path="/admin/transactionHistory" element={<MainLayout><TransactionHistory /></MainLayout>} />
              <Route path="/admin/settings" element={<MainLayout><Settings /></MainLayout>} />
              <Route path="/admin/active-members" element={<MainLayout><ActiveMembers /></MainLayout>} />
              <Route path="/admin/allTransactions" element={<MainLayout><AllTransaction /></MainLayout>} />
              <Route path="/admin/organisation-members" element={<MainLayout><OrganisationMembers /></MainLayout>} />
              <Route path="/admin/organisation-payment" element={<MainLayout><OrganisationPayment /></MainLayout>} />
              <Route path="/admin/organisation-management" element={<MainLayout><OrganisationManagement /></MainLayout>} />
              <Route path="/admin/website" element={<MainLayout><Website /></MainLayout>} />
              <Route path="/admin/template" element={<MainLayout><Template /></MainLayout>} />
              <Route path="/admin/communication" element={<MainLayout><Communication /></MainLayout>} />
              <Route path="/admin/reminders" element={<MainLayout><Reminders /></MainLayout>} />
              <Route path="/admin/reference-tracking" element={<MainLayout><ReferenceTracking /></MainLayout>} />
              
              {/* Expense Module Routes */}
              <Route path="/admin/expenses" element={<MainLayout><ExpensesList /></MainLayout>} />
              <Route path="/admin/expenses/dashboard" element={<MainLayout><ExpensesDashboard /></MainLayout>} />
              <Route path="/admin/expenses/add" element={<MainLayout><ExpenseForm /></MainLayout>} />
              <Route path="/admin/expenses/edit/:id" element={<MainLayout><ExpenseForm /></MainLayout>} />
              <Route path="/admin/expenses/view/:id" element={<MainLayout><ExpenseForm /></MainLayout>} />
              <Route path="/admin/expenses/categories" element={<MainLayout><ExpenseCategories /></MainLayout>} />
              <Route path="/admin/expenses/reports" element={<MainLayout><ExpenseReports /></MainLayout>} />

              {/* Post Generator Module Routes */}
              <Route path="/admin/post-generator/categories" element={<MainLayout><PostCategories /></MainLayout>} />
              <Route path="/admin/post-generator/templates" element={<MainLayout><PostTemplates /></MainLayout>} />
              <Route path="/admin/post-generator/template/add" element={<MainLayout><TemplateEditor /></MainLayout>} />
              <Route path="/admin/post-generator/template/edit/:id" element={<MainLayout><TemplateEditor /></MainLayout>} />
            </Route>
          </Routes>
          </Suspense>
        </ConfirmProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;

